import React, { useState, useEffect } from 'react';
import { Target, Swords, BookOpen, ScrollText, Trophy, Settings, GraduationCap, Zap, DoorOpen, Timer } from 'lucide-react';
import { PlayerProfile, GameSettings } from '../../types';
import { BackButton, Button, Input, ErrorMessage } from '../ui';
import DiceBackground from '../DiceBackground';
import { CONFETTI_DURATION_MS, CONFETTI_PARTICLE_COUNT, MAX_ROOM_CODE_LENGTH } from '../../constants';
import { getSocket, getCurrentSocket } from '../../utils/socket';
import { useTranslation } from '../../i18n';
import Die from '../Die';
import { bgMusic } from '../../music';
import { sounds } from '../../sounds';

// ── Confetti burst component ──────────────────────────────────────────────
const CONFETTI_COLORS = ['#a855f7', '#ef4444', '#eab308', '#06b6d4', '#22c55e', '#f97316', '#ec4899'];

const Confetti: React.FC<{ onDone: () => void }> = ({ onDone }) => {
  const [particles] = useState(() =>
    Array.from({ length: CONFETTI_PARTICLE_COUNT }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      delay: Math.random() * 0.5,
      duration: 1.5 + Math.random() * 2,
      drift: (Math.random() - 0.5) * 80,
      size: 4 + Math.random() * 6,
      rotation: Math.random() * 360,
    }))
  );

  useEffect(() => {
    const timer = setTimeout(onDone, CONFETTI_DURATION_MS);
    return () => clearTimeout(timer);
  }, [onDone]);

  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {particles.map((p) => (
        <div
          key={p.id}
          style={{
            position: 'absolute',
            left: `${p.x}%`,
            top: '-10px',
            width: `${p.size}px`,
            height: `${p.size * 0.6}px`,
            backgroundColor: p.color,
            transform: `rotate(${p.rotation}deg)`,
            animation: `confetti-fall ${p.duration}s ease-in ${p.delay}s forwards`,
            ['--drift' as string]: `${p.drift}px`,
          }}
        />
      ))}
      <style>{`
        @keyframes confetti-fall {
          0% { transform: translateY(0) translateX(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) translateX(var(--drift)) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

interface MenuProps {
  settings: GameSettings;
  profile: PlayerProfile;
  slogan: string;
  expProgress: number;
  expThreshold: number;
  onNavigate: (path: string) => void;
  // Auth state
  isGuest?: boolean;
  // Confetti celebration
  showConfetti?: boolean;
  onConfettiDone?: () => void;
  // Practice mode
  practiceMode?: boolean;
  // Multiplayer mode props (only used when multiplayerMode=true)
  multiplayerMode?: boolean;
  onStartMultiplayer?: (gameMode: 'normal' | 'super-dice') => Promise<void>;
  onJoinRoom?: (roomCode: string) => Promise<void>;
  onQuickMatch?: (gameMode: 'normal' | 'super-dice') => Promise<void>;
  initialError?: string;
}

export const Menu: React.FC<MenuProps> = ({
  settings,
  profile,
  slogan,
  expProgress,
  expThreshold,
  onNavigate,
  isGuest = true,
  showConfetti = false,
  onConfettiDone,
  practiceMode = false,
  multiplayerMode = false,
  onStartMultiplayer,
  onJoinRoom,
  onQuickMatch,
  initialError = '',
}) => {
  const t = useTranslation();

  // Pixel-art friendly line icons instead of OS emojis
  const MenuLabel: React.FC<{ icon: React.ElementType; text: string; big?: boolean }> = ({ icon: Icon, text, big = false }) => (
    <span className="inline-flex items-center justify-center gap-2">
      <Icon className={big ? 'w-3.5 h-3.5 lg:w-5 lg:h-5' : 'w-3 h-3 lg:w-4 lg:h-4'} strokeWidth={2.5} />
      {text}
    </span>
  );

  const [roomCode, setRoomCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(initialError);
  const [selectedGameMode, setSelectedGameMode] = useState<'normal' | 'super-dice'>('normal');
  const [showSuperDicePopup, setShowSuperDicePopup] = useState(false);

  const playClickSound = () => {
    if (!settings.isMuted) {
      sounds.tick();
    }
  };

  useEffect(() => {
    // Only play music on the main menu, not in practice/multiplayer menus
    if (!practiceMode && !multiplayerMode && !settings.isMusicMuted) {
      bgMusic.play();
    } else {
      bgMusic.stop();
    }

    return () => {
      bgMusic.stop();
    };
  }, [practiceMode, multiplayerMode, settings.isMusicMuted]);

  // Auto-dismiss initial error after 4 seconds
  useEffect(() => {
    if (initialError) {
      setError(initialError);
      const timer = setTimeout(() => setError(''), 4000);
      return () => clearTimeout(timer);
    }
  }, [initialError]);

  useEffect(() => {
    // Only show on the main menu view
    if (practiceMode || multiplayerMode) return;

    const hasSeen = localStorage.getItem('hasSeenSuperDiceRelease') === 'true';
    const hasPlayedBefore = profile.exp > 0 || !isGuest || profile.level > 1;

    if (!hasSeen && hasPlayedBefore) {
      setShowSuperDicePopup(true);
    }
  }, [practiceMode, multiplayerMode, profile.exp, profile.level, isGuest]);

  const handleCloseSuperDicePopup = () => {
    localStorage.setItem('hasSeenSuperDiceRelease', 'true');
    setShowSuperDicePopup(false);
  };

  const handlePlaySuperDice = () => {
    localStorage.setItem('hasSeenSuperDiceRelease', 'true');
    setShowSuperDicePopup(false);
    onNavigate('/multiplayer');
  };

  const handleQuickMatch = async () => {
    if (!onQuickMatch) return;
    setIsLoading(true);
    setError('');
    try {
      await onQuickMatch(selectedGameMode);
    } catch (err) {
      setError(t.failedMatch);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateRoom = async () => {
    if (!onStartMultiplayer) return;
    setIsLoading(true);
    setError('');
    try {
      await onStartMultiplayer(selectedGameMode);
    } catch (err) {
      setError(t.failedCreateRoom);
    } finally {
      setIsLoading(false);
    }
  };

  const handleJoinRoom = async () => {
    if (!onJoinRoom) return;
    if (!roomCode.trim()) {
      setError(t.enterRoomCode);
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      await onJoinRoom(roomCode.trim().toUpperCase());
    } catch (err) {
      const msg = (err as Error).message || '';
      if (msg.includes('full')) setError(t.roomFull);
      else if (msg.includes('started')) setError(t.gameAlreadyStarted);
      else if (msg.includes('completed')) setError(t.roomNoLongerExists);
      else setError(t.roomNotFound);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Online player count (from WebSocket server) ────────────────────────
  const [onlineCount, setOnlineCount] = useState(0);

  useEffect(() => {
    if (!multiplayerMode) return;

    let cancelled = false;
    let boundSock: ReturnType<typeof getCurrentSocket> = null;

    const handler = (data: { count: number }) => {
      setOnlineCount(data.count);
    };

    (async () => {
      try {
        const sock = await getSocket();
        if (cancelled) return;
        boundSock = sock;
        sock.on('online_count', handler);
      } catch {
        // Not connected yet, ignore
      }
    })();

    return () => {
      cancelled = true;
      if (boundSock) {
        boundSock.off('online_count', handler);
      }
    };
  }, [multiplayerMode]);

  // ── Practice Mode State ────────────────────────────────────────────────
  const [timeoutEnabled, setTimeoutEnabled] = useState(false);
  const [timeoutSeconds, setTimeoutSeconds] = useState(5);

  const buildPlayPath = (diff: string) => {
    const base = `/play/${diff}`;
    if (timeoutEnabled) return `${base}?timeout=${timeoutSeconds}`;
    return base;
  };

  // ── Practice Mode View ─────────────────────────────────────────────────
  if (practiceMode) {
    return (
      <div className="h-[100dvh] flex flex-col items-center justify-center text-white p-4 relative select-none bg-[#1a101f] overflow-hidden">
        <DiceBackground count={14} />
        <BackButton onClick={() => { playClickSound(); onNavigate('/'); }} />

        <h1 className="text-2xl lg:text-5xl font-bold tracking-tighter mb-4 text-theme drop-shadow-lg uppercase text-center relative z-10">
          {t.practice}
        </h1>
        <p className="text-[8px] lg:text-xs text-gray-500 mb-6 tracking-widest font-mono text-center uppercase relative z-10">
          {t.practiceNoExp}
        </p>

        <div className="w-full max-w-xs lg:max-w-md flex flex-col gap-4 relative z-10">
          <button
            onClick={() => { playClickSound(); onNavigate('/tutorial'); }}
            className="w-full px-4 py-3 lg:px-8 lg:py-5 pixel-border bg-theme/20 border-2 border-theme/50 text-theme text-[9px] lg:text-sm font-bold uppercase tracking-widest hover:bg-theme/30 active:translate-y-1 transition-all"
          >
            <MenuLabel icon={GraduationCap} text={t.tutTitle} />
          </button>

          <div className="w-full h-px bg-gray-700/50" />

          <Button onClick={() => { playClickSound(); onNavigate(buildPlayPath('easy')); }} variant="success" size="lg">
            {t.easy}
          </Button>
          <Button onClick={() => { playClickSound(); onNavigate(buildPlayPath('medium')); }} variant="primary" size="lg">
            {t.normal}
          </Button>
          <Button onClick={() => { playClickSound(); onNavigate(buildPlayPath('hard')); }} variant="danger" size="lg">
            {t.hard}
          </Button>

          {/* ── Timeout Toggle ──────────────────────────────────────── */}
          <div className="mt-4 pixel-card p-4 flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center gap-1.5 text-[9px] lg:text-xs font-bold uppercase tracking-wider text-gray-300">
                <Timer className="w-3 h-3 lg:w-4 lg:h-4" strokeWidth={2.5} />
                {t.turnTimeout}
              </span>
              <button
                onClick={() => { playClickSound(); setTimeoutEnabled(!timeoutEnabled); }}
                className={`relative w-10 h-5 lg:w-12 lg:h-6 rounded-full transition-colors duration-200 ${timeoutEnabled ? 'bg-theme' : 'bg-gray-700'
                  }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-4 h-4 lg:w-5 lg:h-5 rounded-full bg-white shadow transition-transform duration-200 ${timeoutEnabled ? 'translate-x-5 lg:translate-x-6' : 'translate-x-0'
                    }`}
                />
              </button>
            </div>

            {timeoutEnabled && (
              <div className="flex items-center gap-3">
                <span className="text-[8px] lg:text-[10px] text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  {t.seconds}
                </span>
                <div className="flex items-center gap-2 flex-1 justify-center">
                  <button
                    onClick={() => { playClickSound(); setTimeoutSeconds(Math.max(3, timeoutSeconds - 1)); }}
                    className="w-7 h-7 lg:w-8 lg:h-8 pixel-border bg-gray-800 text-white text-xs lg:text-sm font-bold hover:bg-gray-700 transition-colors flex items-center justify-center"
                  >
                    −
                  </button>
                  <span className="text-lg lg:text-2xl font-bold text-theme w-8 text-center tabular-nums">
                    {timeoutSeconds}
                  </span>
                  <button
                    onClick={() => { playClickSound(); setTimeoutSeconds(Math.min(15, timeoutSeconds + 1)); }}
                    className="w-7 h-7 lg:w-8 lg:h-8 pixel-border bg-gray-800 text-white text-xs lg:text-sm font-bold hover:bg-gray-700 transition-colors flex items-center justify-center"
                  >
                    +
                  </button>
                </div>
                <span className="text-[8px] lg:text-[10px] text-gray-400">
                  {timeoutSeconds}s
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Multiplayer Lobby View ──────────────────────────────────────────────
  if (multiplayerMode) {
    return (
      <div className="h-[100dvh] flex flex-col items-center justify-center text-white p-4 relative select-none bg-[#1a101f] overflow-hidden">
        <DiceBackground count={14} />
        <BackButton onClick={() => { playClickSound(); onNavigate('/'); }} />

        <h1 className="text-2xl lg:text-5xl font-bold tracking-tighter mb-10 text-theme drop-shadow-lg uppercase text-center relative z-10">
          {t.multiplayer}
        </h1>

        <div className="flex items-center justify-center gap-2 mb-8 relative z-10">
          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <span className="text-[9px] lg:text-xs text-green-400 font-bold uppercase tracking-wider">
            {onlineCount > 0
              ? t.playerOnline(onlineCount)
              : <span className="animate-pulse text-gray-500">{t.loading}</span>
            }
          </span>
        </div>

        <div className="w-full max-w-xs lg:max-w-md flex flex-col gap-6 relative z-10">
          {error && <ErrorMessage>{error}</ErrorMessage>}

          <div className="flex gap-2 w-full">
            <button
              onClick={() => { playClickSound(); setSelectedGameMode('normal'); }}
              className={`flex-1 py-3 lg:py-4 pixel-border text-[10px] lg:text-sm font-bold uppercase transition-colors ${selectedGameMode === 'normal' ? 'bg-theme/40 text-theme border-theme border-2 shadow-[0_0_15px_rgba(234,179,8,0.5)]' : 'bg-gray-900 border-2 border-gray-700 text-gray-500 hover:bg-gray-800'}`}
            >
              Normal
            </button>
            <button
              onClick={() => { playClickSound(); setSelectedGameMode('super-dice'); }}
              className={`flex-1 py-3 lg:py-4 pixel-border text-[10px] lg:text-sm font-bold uppercase transition-colors relative overflow-hidden ${selectedGameMode === 'super-dice' ? 'bg-gradient-to-r from-purple-500/40 via-pink-500/40 to-yellow-500/40 border-2 border-pink-400 text-pink-300 shadow-[0_0_15px_rgba(236,72,153,0.5)]' : 'bg-gray-900 border-2 border-gray-700 text-gray-500 hover:bg-gray-800'}`}
            >
              Super DIce
            </button>
          </div>

          <Button onClick={() => { playClickSound(); handleQuickMatch(); }} variant="primary" size="lg">
            {isLoading ? t.searching : <MenuLabel icon={Zap} text={t.quickMatch} big />}
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-700"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="px-4 bg-[#1a101f] text-gray-500 text-xs">{t.or}</span>
            </div>
          </div>

          <Button onClick={() => { playClickSound(); handleCreateRoom(); }} variant="success" size="lg">
            <MenuLabel icon={DoorOpen} text={t.createRoom} big />
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-700"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="px-4 bg-[#1a101f] text-gray-500 text-xs">{t.or}</span>
            </div>
          </div>

          <div className="flex gap-2">
            <Input
              type="text"
              maxLength={MAX_ROOM_CODE_LENGTH}
              placeholder={t.roomCode}
              value={roomCode}
              onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
              className="flex-1 uppercase text-center tracking-widest"
            />
            <Button
              onClick={() => { playClickSound(); handleJoinRoom(); }}
              disabled={isLoading || !roomCode.trim()}
              variant="secondary"
              size="md"
            >
              {t.join}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Main Menu View ──────────────────────────────────────────────────────
  return (
    <div className="h-[100dvh] flex flex-col items-center justify-center text-white p-4 relative select-none bg-[#1a101f] overflow-hidden">
      {showConfetti && onConfettiDone && <Confetti onDone={onConfettiDone} />}
      {showSuperDicePopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
          <div className="pixel-card bg-[#1a101f] border-2 border-theme/50 p-6 lg:p-10 flex flex-col items-center gap-4 lg:gap-6 max-w-sm w-full mx-4 shadow-[0_0_30px_rgba(234,179,8,0.2)]">
            <h2 className="text-xl lg:text-3xl font-bold bg-gradient-to-r from-red-500 via-yellow-500 via-green-500 via-blue-500 to-purple-500 text-transparent bg-clip-text animate-pulse uppercase tracking-widest text-center shadow-sm">
              {t.superDiceReleasePopupTitle}
            </h2>
            <div className="flex gap-2 mb-2">
              <Die value="joker" size="sm" />
              <Die value="shield" size="sm" />
              <Die value="bomb" size="sm" />
              <Die value="reroll" size="sm" />
            </div>
            <p className="text-[10px] lg:text-sm text-gray-300 text-center uppercase leading-relaxed font-bold">
              {t.superDiceReleasePopupText}
            </p>
            <div className="flex gap-3 w-full mt-2">
              <button
                onClick={handleCloseSuperDicePopup}
                className="flex-1 px-3 py-3 pixel-border bg-gray-900 border-2 border-gray-700 text-[8px] lg:text-xs text-gray-400 font-bold uppercase hover:bg-gray-800 transition-all"
              >
                {t.superDiceReleasePopupClose}
              </button>
              <button
                onClick={handlePlaySuperDice}
                className="flex-1 px-3 py-3 pixel-border bg-gradient-to-r from-purple-500 via-pink-500 to-yellow-500 text-[8px] lg:text-xs text-white font-bold uppercase hover:brightness-110 transition-all shadow-lg"
              >
                {t.superDiceReleasePopupPlay}
              </button>
            </div>
          </div>
        </div>
      )}
      <DiceBackground />
      <div className="absolute top-8 w-full max-w-xs lg:max-w-md px-4 z-10">
        <div className="flex justify-between items-end mb-1">
          <span className="text-[8px] lg:text-xs text-theme font-bold">
            {profile.name ? profile.name : <span className="animate-pulse text-gray-500">{t.loading}</span>} • {t.lvlFormat(profile.level)}
          </span>
          <div className="flex items-end gap-2">
            <span className="text-[8px] lg:text-[10px] text-gray-500 font-mono">
              {t.expFormat(profile.exp, expThreshold)}
            </span>
            {isGuest ? (
              <button
                onClick={() => { playClickSound(); onNavigate('/auth'); }}
                className="text-[8px] lg:text-[10px] font-bold uppercase tracking-wider text-yellow-400 hover:text-yellow-300 transition-colors bg-yellow-900/30 hover:bg-yellow-800/40 px-2 py-0.5 pixel-border"
              >
                {t.signUp}
              </button>
            ) : (
              <span className="text-[8px] lg:text-[10px] font-bold uppercase tracking-wider text-green-400 bg-green-900/30 px-2 py-0.5 pixel-border">
                ✓
              </span>
            )}
          </div>
        </div>
        <div className="w-full h-2 lg:h-3 bg-black/40 border-2 border-theme overflow-hidden pixel-border p-0">
          <div className="h-full bg-theme transition-all duration-500" style={{ width: `${expProgress}%` }} />
        </div>
      </div>

      <h1 className="text-4xl lg:text-7xl font-bold tracking-tighter mb-2 text-theme drop-shadow-[0_5px_0_rgba(0,0,0,0.5)] text-center uppercase mt-12 relative z-10">
        {t.dicepit}
      </h1>
      <p className="text-[10px] lg:text-sm text-theme mb-8 tracking-widest animate-pulse font-mono text-center h-4 flex items-center justify-center uppercase relative z-10">
        {slogan}
      </p>

      <div className="flex flex-col gap-3 w-full max-w-xs lg:max-w-md relative z-10">
        <Button onClick={() => { playClickSound(); onNavigate('/practice'); }} variant="primary" size="md">
          <MenuLabel icon={Target} text={t.menuPractice} big />
        </Button>
        <Button onClick={() => { playClickSound(); onNavigate('/multiplayer'); }} variant="warning" size="lg">
          <MenuLabel icon={Swords} text={t.menuMultiplayer} big />
        </Button>

        <div className="h-2 lg:h-4"></div>
        <Button onClick={() => { playClickSound(); onNavigate('/how-to-play'); }} variant="secondary" size="sm">
          <MenuLabel icon={BookOpen} text={t.menuHowToPlay} />
        </Button>
        <Button onClick={() => { playClickSound(); onNavigate('/history'); }} variant="secondary" size="sm">
          <MenuLabel icon={ScrollText} text={t.menuRecords} />
        </Button>
        <Button onClick={() => { playClickSound(); onNavigate('/leaderboard'); }} variant="secondary" size="sm">
          <MenuLabel icon={Trophy} text={t.menuLeaderboard} />
        </Button>
        <Button onClick={() => { playClickSound(); onNavigate('/settings'); }} variant="secondary" size="sm">
          <MenuLabel icon={Settings} text={t.menuSettings} />
        </Button>
      </div>

      <div className="absolute bottom-6 flex items-center gap-3 z-10">
        <a
          href="https://rizasabuncu.com"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[8px] lg:text-[10px] font-mono opacity-40 uppercase tracking-widest hover:opacity-100 hover:text-theme transition-all duration-300 decoration-dotted underline-offset-4 hover:underline"
        >
          2026 © RIZASABUNCU
        </a>
        <span className="text-gray-700 text-[8px]">|</span>
        <button
          onClick={() => { playClickSound(); onNavigate('/legal'); }}
          className="text-[8px] lg:text-[10px] font-mono opacity-40 uppercase tracking-widest hover:opacity-100 hover:text-theme transition-all duration-300"
        >
          {t.legal}
        </button>
        <span className="text-gray-700 text-[8px]">|</span>
        <button
          onClick={() => { playClickSound(); onNavigate('/security'); }}
          className="text-[8px] lg:text-[10px] font-mono opacity-40 uppercase tracking-widest hover:opacity-100 hover:text-theme transition-all duration-300"
        >
          {t.security}
        </button>
      </div>
    </div>
  );
};
